import { IRect } from "konva/lib/types";
import { InstallationType, IntegratedDoorPosition, NullableNumber, NullableString, OpeningDirection, OpeningMode, Optional, SpringType, StepSlugsEnum } from "../core/common_types";
import { CategoryCode } from "./category";

export type DoorOptionType = "larghezza_standard" | "larghezza_standard_minore" | "larghezza_standard_maggiore" | "altezza_standard" | "altezza_standard_minore" | "altezza_standard_maggiore" | "altezza_superiore" | "non_standard";

export type ColorSide = "internal" | "external";
export type ColorType = "ral_standard" | "ral_non_standard" | "a_campione" | "art_coat";
export type IntegratedDoorMeasureType = "undefined" | "standard" | "900" | "1200";
export type FrameInternalColorType = "same_as_external" | "different_from_external";
export type DownPaymentType = "percentage" | "amount";
export type InputType = "ral" | "number";

export type TelaioPerimetraleSku = "ACCX09" | "ACCX01" | "ACCX08";

export const TELAIO_PERIMETRALE_DEFAULT_SKU: TelaioPerimetraleSku = "ACCX09";

// RAL di ciascuna variante; null per ACCX08 ("RAL a scelta"), dove il RAL lo indica il cliente.
// Associazione sku -> RAL fissa lato backend (AccessoriesEnum), replicata qui per il render 3D.
export const TELAIO_PERIMETRALE_RAL_BY_SKU: Record<TelaioPerimetraleSku, NullableString> = {
    ACCX09: "9016",
    ACCX01: "7016",
    ACCX08: null,
};

export type TelaioPerimetraleConfiguration = {
    sku: TelaioPerimetraleSku,
    // RAL scelto dal cliente, valorizzato solo quando sku e' "ACCX08"
    ral: NullableString,
}
export enum WindowSize {
    undefined = "undefined",
    _540x360 = "540x360",
    _230x230 = "230x230",
    glasspanel = "glasspanel",
    glasspanel_vetro_6_7 = "glasspanel_vetro_6_7",
    glasspanel_vetro_5 = "glasspanel_vetro_5",
    glasspanel_vetro_4_12_4 = "glasspanel_vetro_4_12_4",
};
export enum GridSize {
    undefined = "undefined",
    _344x138 = "344x138",
    _400x200 = "400x200",
    superificie_ft = "superificie_ft",
    superificie_fq = "superificie_fq"
}

export enum GlassType {
    vetro_camera_4_12_4_trasparente = "vetro_camera_4_12_4_trasparente",
    vetro_camera_4_12_4_satinato = "vetro_camera_4_12_4_satinato",
    vetro_6_7_sicurezza_trasparente = "vetro_6_7_sicurezza_trasparente", 
    vetro_6_7_sicurezza_satinato_bianco = "vetro_6_7_sicurezza_satinato_bianco",
    vetro_6_7_sicurezza_grigio_fume_trasparente = "vetro_6_7_sicurezza_grigio_fume_trasparente", 
    vetro_6_7_sicurezza_bronzo_trasparente = "vetro_6_7_sicurezza_bronzo_trasparente",
    vetro_camera_temperato_4_12_4_trasparente = "vetro_camera_temperato_4_12_4_trasparente",
    vetro_camera_temperato_4_12_4_satinato_bianco = "vetro_camera_temperato_4_12_4_satinato_bianco"
};

export type FormDimensioniModel = {
    quantita: number,
    vanoConAltroAccesso: boolean,
    larghezzaForo: number,
    altezzaForo: number,
    larghezzaPorta: number,
    altezzaPorta: number,
    tipoLarghezza: DoorOptionType,
    tipoAltezza: DoorOptionType,
    profonditaGarage: number,
    altezzaArchitrave: number,
    spessoreArchitrave: number,
    larghezzaSpallettaSx: number,
    spessoreSpallettaSx: number,
    larghezzaSpallettaDx: number,
    spessoreSpallettaDx: number,
    tipoInstallazioneMontanteDx: InstallationType,
    tipoInstallazioneMontanteSx: InstallationType,
    tipoInstallazioneTraversa: InstallationType,
    tipoMolle: SpringType,
    isMotorizzata: boolean,
}

export type FormAspettoModel = {
    porta: NullableNumber,
    modello: NullableNumber,
    finitura: NullableNumber,
    tipoColoreEsterno: ColorType,
    coloreEsterno: NullableNumber,
    coloreEsternoNsRal: NullableString,
    coloreEsternoACampione: boolean,
    tipoColoreInterno: ColorType,
    coloreInterno: NullableNumber,
    coloreInternoNsRal: NullableString,
    coloreInternoACampione: boolean,
    finituraVetro: NullableNumber,
    coloreTelaioInternoDiverso: boolean,
    finestratura: WindowingConfiguration|null,
    ventilazione: AiringConfiguration|null,
    portinaIntegrata: IntegratedDoorConfiguration|null,
    portinaAffiancata: ExternalDoorConfiguration|null,
    artCoatPreviewImage: NullableString,
}

export type PanelConfiguration = {panelNumber: number, elementsNumber: number, position: number, bbox: IRect};

export type AiringConfiguration = {
    gridSize: GridSize,
    positioning: PanelConfiguration[],
    quantity: NullableNumber,
    externalColorType: ColorType,
    externalColorCode: NullableString,
    internalColorType: ColorType,
    internalColorCode: NullableString,
}

export type WindowingConfiguration = {
    windowSize: WindowSize,
    positioning: PanelConfiguration[],
    quantity: NullableNumber,
    externalFrameColorType: ColorType,
    externalFrameColorCode: NullableString,
    internalFrameColorType: ColorType,
    internalFrameColorCode: NullableString,
    glassFinish: GlassType|undefined
}

export type DettaglioMisura = {esternoTelaio: number, internoTelaio: number};

export type IntegratedDoorConfiguration = {
    portina: boolean,
    bbox: IRect|null,
    larghezza: DettaglioMisura|null,
    altezza: DettaglioMisura|null,
    externalFrameColorType: ColorType,
    externalFrameColorCode: NullableString,
    internalFrameColorType: ColorType,
    internalFrameColorCode: NullableString,
    externalHandleColorType: ColorType,
    externalHandleColorCode: NullableString,
    internalHandleColorType: ColorType,
    internalHandleColorCode: NullableString,
    posizione: IntegratedDoorPosition,
    apertura: OpeningDirection,
    fuoriMisura: IntegratedDoorMeasureType,
    accessori: Optional[],
    soglia: OpzioniSoglia
}

export type OpzioniSoglia = {
    rialzata: boolean,
    altezza: number|null
}

export type ExternalDoorConfiguration = {
    portina: boolean,
    larghezza: number,
    altezza: number,
    qta: number|null,
    usaStessaFinitura: boolean,
    porta: Option,
    modello: Option,
    finitura: Option,
    tipoColoreEsterno: ColorType,
    coloreEsterno: NullableString,
    tipoColoreInterno: ColorType,
    coloreInterno: NullableString,
    externalFrameColorType: ColorType,
    externalFrameColorCode: NullableString,
    internalFrameColorType: ColorType,
    internalFrameColorCode: NullableString,
    externalHandleColorType: ColorType,
    externalHandleColorCode: NullableString,
    internalHandleColorType: ColorType,
    internalHandleColorCode: NullableString,
    finituraVetro: Option,
    finituraLamiera: Option,
    usaColoreTelaioInternoComeEsterno: boolean,
    tipoInstallazione: InstallationType,
    tipoApertura: OpeningMode,
    versoApertura: OpeningDirection,
    accessori: Optional[]
}

export interface OverlapConfiguration {

}

export interface DoorConfiguration extends OverlapConfiguration {
    categoryCode: CategoryCode,
    categoryId: number,
    configurationId: number,
    quantita: number,
    larghezzaForo: number,
    altezzaForo: number,
    larghezzaPorta: number,
    altezzaPorta: number,
    vanoConAltroAccesso: boolean,
    portinaPedonaleIntegrata: boolean,
    profonditaGarage: number,
    altezzaArchitrave: number,
    spessoreArchitrave: number,
    larghezzaSpallettaSx: number,
    spessoreSpallettaSx: number,
    larghezzaSpallettaDx: number,
    spessoreSpallettaDx: number,
    tipoMolle: SpringType,
    tipoInstallazioneMontanteDx: InstallationType,
    tipoInstallazioneMontanteSx: InstallationType,
    tipoInstallazioneTraversa: InstallationType,
    porta: NullableNumber,
    modello: NullableNumber,
    finitura: NullableNumber,
    finituraVetro: NullableNumber,
    tipoColoreEsterno: ColorType,
    coloreEsterno: NullableNumber,
    coloreEsternoNsRal: NullableString,
    coloreEsternoACampione: boolean,
    tipoColoreInterno: ColorType,
    coloreInterno: NullableNumber,
    coloreInternoNsRal: NullableString,
    coloreInternoACampione: boolean,
    coloreTelaioInternoDiverso: boolean,
    artCoatPreviewImage: NullableString,
    isMotorizzata: boolean,
    isDebordante: boolean,
    finestratura: WindowingConfiguration|null,
    windowFrameColorId: NullableNumber,
    windowFrameColorNsRal: NullableString,
    ventilazione: AiringConfiguration|null,
    portinaIntegrata: IntegratedDoorConfiguration|null,
    portinaAffiancata: ExternalDoorConfiguration|null,
    maniglieria: HandleConfiguration|null,
    telaioPerimetrale: TelaioPerimetraleConfiguration|null,
    isFinito: boolean,
    stepAttuale: StepSlugsEnum
}

export type DoorOption = {
    proprieta: string,
    valore: number,
    tipo: DoorOptionType,
    valorePartenza: number,
    tubolare: Tubular | null,
    isSelected: boolean
}

export type DoorConfig = {
    integratedDoorSupport: boolean
} | null;

export type Tubular = {
    width: number,
    height: number
}

export type Option = {
    id: number,
    optionId: number,
    parentId: NullableNumber,
    value: NullableString,    
    name: string,
    type: string
} | null | undefined;

export type Color = {
    id: number,
    code: string,
    name: string,
    img: NullableString,
    sortOrder: number,
    additionalInfo: NullableString,
    parentOptionId: number,
    side: ColorSide
} | null | undefined;

export type Accessorio = {
    id: number,
    sku: string,
    name: NullableString,
    price: number,
    um: NullableString,
    img: NullableString,
    qtyMax: number,
    inputType: InputType
} | null | undefined;

export type AccessorioInConfigurazione = {
    accessoryId: number,
    qty: number,
    value: string|null|undefined
}

export type IntegratedDoorOptions = {
    descrizione: string,
    fuoriMisura: Accessorio[],
    accessoriVari: Accessorio[],
}

export type ExternalDoorOptions = {
    accessoriVari: Accessorio[],
    verniciaturaNonStandard: Accessorio[],
}

export type WindowingOptions = {
    windows: Accessorio[],
    verniciaturaNonStandard: Accessorio[],
}

export type AiringOptions = {
    grids: Accessorio[],
    verniciaturaNonStandard: Accessorio[],
}

export type TipoManiglia = "DOPPIA" | "SBLOCCO";

export type HandleConfiguration = {
    tipoManiglia: TipoManiglia | null,
    verniciatura: NullableNumber,
    // RAL scelto quando la verniciatura selezionata e' "RAL A SCELTA"
    verniciaturaRal?: NullableString,
}

export type HandleOptions = {
    verniciature: Accessorio[],
    sblocco: Accessorio,
}

export type DoorConfigurationConditions = {
    customerId: NullableString,
    customClientName: NullableString,
    customClientAddress: NullableString,
    customClientZipCode: NullableString,
    customClientCity: NullableString,
    customClientProvince: NullableString,
    customClientCountry: NullableString,
    endClientDownPaymentType: DownPaymentType,
    endClientDownPaymentValue: NullableNumber,
    endClientDiscount1: NullableNumber,
    endClientDiscount2: NullableNumber,
    endClientNet: NullableNumber,
    endClientTaxRate: NullableNumber,
    endClientTaxableAmount: NullableNumber,
    endClientTotalTaxes: NullableNumber,
    endClientTotalDiscount: NullableNumber,
    endClientTotal: NullableNumber,
    retailTaxRate: NullableNumber,
    retailDownPaymentType: DownPaymentType,
    retailDownPaymentValue: NullableNumber,
    retailDiscount1: NullableNumber,
    retailDiscount2: NullableNumber,
    retailNet: NullableNumber,
    retailTaxableAmount: NullableNumber,
    retailTotalTaxes: NullableNumber,
    retailTotalDiscount: NullableNumber,
    retailTotal: NullableNumber,
    retailInstallation: "include" | "exclude",
    retailTransport: "include" | "exclude",
    baseNetPrice: NullableNumber,
    totalServices: NullableNumber,
    retailInstallationCosts: NullableNumber,
    quantity: NullableNumber,
    notes: NullableString,
    isServiceCustomer: boolean,
    packagingType: "cartone" | "legno" | null,
};

export type QuoteData = {
    id: number,
    creationDate: NullableString,
    agentName: NullableString,
    agentSurname: NullableString,
    clientName: NullableString,
    clientSurname: NullableString,
    clientAddress: NullableString,
    clientZipCode: NullableString,
    clientCity: NullableString,
    clientPhone: NullableString,
    clientCode: NullableString,
    clientVatNumber: NullableString,
    clientEmail: NullableString,
    architrave: NullableNumber,
    height: NullableNumber,
    width: NullableNumber,
    externalFrameHeight: NullableNumber,
    externalFrameWidth: NullableNumber,
    sxStileWidth: NullableNumber,
    dxStileWidth: NullableNumber,
    sku: NullableString,
    description: NullableString,
    qty: NullableNumber,
    uom: NullableString,
    baseNetPrice: NullableNumber,
    productPrice: NullableNumber,
    totalServices: NullableNumber,
    notes: NullableString,
    crossbeamInstallationType: NullableNumber,
    dxPillarInstallationType: NullableNumber,
    sxPillarInstallationType: NullableNumber,
    externalColorType: NullableString,
    externalColorNsRal: NullableString,
    externalColorRal: NullableString,
    externalColorName: NullableString,
    internalColorRal: NullableString,
    internalColorName: NullableString,
    withIntegratedDoor: boolean,
    widthSideDoor: boolean,
    hasOverflow: boolean,
    imageRendering: NullableString,
    isServiceCustomer: boolean,
    endClientDownPaymentType: NullableString,
    endClientDownPaymentValue: NullableNumber,
    endClientDiscount1: NullableNumber,
    endClientDiscount2: NullableNumber,
    endClientNet: NullableNumber,
    endClientTaxRate: NullableNumber,
    endClientTaxableAmount: NullableNumber,
    endClientTotalTaxes: NullableNumber,
    endClientTotalDiscount: NullableNumber,
    endClientTotal: NullableNumber,
    retailTaxRate: NullableNumber,
    retailDownPaymentType: NullableNumber,
    retailDownPaymentValue: NullableNumber,
    retailDiscount1: NullableNumber,
    retailDiscount2: NullableNumber,
    retailNet: NullableNumber,
    retailTaxableAmount: NullableNumber,
    retailTotalTaxes: NullableNumber,
    retailTotalDiscount: NullableNumber,
    retailTotal: NullableNumber,
    retailInstallationCosts: NullableNumber,
    transportAmount: NullableNumber,
    packagingAmount: NullableNumber,
    syncStatus: NullableString,
    status: NullableString,
}

export type QuoteAccessory = {
    id: number,
    qty: number
    sku: NullableString,
    name: NullableString,
    price: NullableNumber,
    um: NullableString,
    net: NullableNumber,
    additionalData: NullableString
}

export type QuoteSummary = {
    data: QuoteData,
    accessories: QuoteAccessory[],    
} | null;